import { useEffect, useState } from 'react';
import AppShell from '../app/AppShell';
import { FeedbackMessage, SaveConfirmationModal, ToastStack } from '../components/common';
import {
  EntityEditModal,
  EntityFilters,
  EntityForm,
  EntityTable,
  EntityToolbar,
} from '../components/entity';
import { customerFormInitialValues, customerStatuses } from '../data/customerConstants';
import useCancelConfirmation from '../hooks/useCancelConfirmation';
import useCustomers from '../hooks/useCustomers';
import useSaveConfirmation from '../hooks/useSaveConfirmation';
import { hasChangedFormValues } from '../utils/formDirty';

function CustomerPage({ navigationItems, activePath, activeSearch = '', onNavigate }) {
  const saveConfirmation = useSaveConfirmation();
  const cancelConfirmation = useCancelConfirmation();
  const [deletingCustomer, setDeletingCustomer] = useState(null);
  const {
    activeView,
    filterValues,
    customerSearchSuggestions,
    formValues,
    formErrors,
    filteredCustomers,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    editingCustomer,
    editValues,
    editErrors,
    editError,
    isEditSaving,
    setActiveView,
    clearFormPhoto,
    handleFormChange,
    handleFilterChange,
    handleFilterSubmit,
    handleFilterReset,
    handleSubmit,
    handleCancel,
    openEditModal,
    closeEditModal,
    clearEditPhoto,
    handleEditChange,
    handleEditSubmit,
    handleDelete,
  } = useCustomers();
  const toasts = [
    loadError ? { id: 'customer-load-error', tone: 'error', message: loadError } : null,
    submitError ? { id: 'customer-submit-error', tone: 'error', message: submitError } : null,
    successMessage ? { id: 'customer-success', tone: 'success', message: successMessage } : null,
    editError ? { id: 'customer-edit-error', tone: 'error', message: editError } : null,
  ].filter(Boolean);

  useEffect(() => {
    const searchParams = new URLSearchParams(activeSearch);

    if (searchParams.get('view') === 'create') {
      setActiveView('create');
    }
  }, [activeSearch, setActiveView]);

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      <section className="mt-5 px-4 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <EntityToolbar
            activeView={activeView}
            onChangeView={setActiveView}
            entityLabel="Customer"
          />

          {activeView === 'list' ? (
            <>
              <EntityFilters
                values={filterValues}
                onChange={handleFilterChange}
                onSubmit={handleFilterSubmit}
                onReset={handleFilterReset}
                statusOptions={customerStatuses}
                searchFieldName="customerName"
                searchLabel="Search"
                showDateFilter={false}
                showApply={false}
                searchSuggestions={customerSearchSuggestions}
              />

              <div className="grid gap-3">
                <div>
                  <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.4rem]">
                    Customer List
                  </h2>
                  <p className="m-0 mt-2 text-[0.96rem] text-[var(--theme-text-muted)]">
                    Manage customer contact records and account status.
                  </p>
                </div>

                {isLoading ? (
                  <FeedbackMessage tone="info" message="Loading customer records..." />
                ) : (
                  <EntityTable
                    records={filteredCustomers}
                    onEditRecord={openEditModal}
                    onDeleteRecord={setDeletingCustomer}
                    entityLabel="Customer"
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <EntityForm
                values={formValues}
                errors={formErrors}
                isSaving={isSaving}
                onChange={handleFormChange}
                onClearPhoto={clearFormPhoto}
                onSubmit={(event) =>
                  saveConfirmation.requestConfirmation(event, handleSubmit, {
                    title: 'Save customer profile?',
                    message: 'Please confirm the customer details before saving.',
                    confirmLabel: 'Save Customer',
                  })
                }
                onCancel={(event) =>
                  cancelConfirmation.requestCancel(event, {
                    shouldConfirm: () =>
                      hasChangedFormValues(formValues, customerFormInitialValues),
                    onCancel: handleCancel,
                    title: 'Cancel customer profile?',
                    message: 'You have unsaved customer inputs. Discard them?',
                  })
                }
                entityLabel="Customer"
              />
            </>
          )}
        </div>
      </section>

      {editingCustomer ? (
        <EntityEditModal
          values={editValues}
          errors={editErrors}
          statusOptions={customerStatuses}
          isSaving={isEditSaving}
          onChange={handleEditChange}
          onClearPhoto={clearEditPhoto}
          onClose={(event) =>
            cancelConfirmation.requestCancel(event, {
              shouldConfirm: () =>
                editingCustomer
                  ? hasChangedFormValues(editValues, getCustomerEditBaseline(editingCustomer))
                  : false,
              onCancel: closeEditModal,
              title: 'Cancel editing?',
              message: 'You have unsaved customer changes. Discard them?',
            })
          }
          onSubmit={(event) =>
            saveConfirmation.requestConfirmation(event, handleEditSubmit, {
              title: 'Save customer changes?',
              message: 'Please confirm the updated customer details before saving.',
              confirmLabel: 'Save Changes',
            })
          }
          entityLabel="Customer"
        />
      ) : null}

      {deletingCustomer ? (
        <SaveConfirmationModal
          isOpen
          title="Delete customer?"
          message={`This will permanently delete ${deletingCustomer.companyName}. Continue?`}
          confirmLabel="Delete Customer"
          confirmingLabel="Deleting..."
          cancelLabel="Keep Customer"
          isConfirming={isSaving}
          onCancel={() => setDeletingCustomer(null)}
          onConfirm={async () => {
            const didDelete = await handleDelete(deletingCustomer);

            if (didDelete) {
              setDeletingCustomer(null);
            }
          }}
        />
      ) : null}

      <SaveConfirmationModal {...saveConfirmation.modalProps} />
      <SaveConfirmationModal {...cancelConfirmation.modalProps} />
    </AppShell>
  );
}

function getCustomerEditBaseline(customer) {
  return {
    companyName: customer.companyName,
    contactNumber: customer.contactNumber || '',
    branchLocations: customer.branchLocations,
    photoUrl: customer.photoUrl || '',
    status: customer.status,
  };
}

export default CustomerPage;
